import { AxiosError } from 'axios';
import i18n from '@/i18n';
import { del, get, post, put } from '@/utils';
import {
	IServerColumnConfig,
	IServerTableConfig
} from '@/components/table/column-config';
import { IAnnouncementType, IAnnouncementTypePayload } from './types';

const BASE = 'api/announcement-types';

type RawType = {
	id?: number | string;
	name?: string;
	color?: string;
	icon?: string;
	position?: number;
	default?: boolean;
	description?: string | null;
	createdAt?: string;
};

type TypesResponse = {
	items?: RawType[];
	totalPages?: number;
	totalCount?: number;
};

const normalizeType = (raw: RawType, index: number): IAnnouncementType => ({
	id: raw.id ?? index,
	name: String(raw.name || ''),
	color: raw.color || undefined,
	icon: raw.icon || undefined,
	isDefault: Boolean(raw.default),
	position: typeof raw.position === 'number' ? raw.position : index,
	description: raw.description ?? undefined,
	createdAt: raw.createdAt ?? undefined
});

const buildQuery = (query: string, page: number, items: number): string => {
	const params = new URLSearchParams((query || '').replace(/^\?/, ''));
	params.set('PageNumber', String(page + 1));
	params.set('PageSize', String(items));
	return `?${params.toString()}`;
};

// BE → payload: câmpul e `default` (nu `isDefault`). Ambele trimit `description`; PUT trimite în plus `position`.
const basePayload = (payload: IAnnouncementTypePayload): Record<string, unknown> => ({
	name: payload.name,
	color: payload.color,
	icon: payload.icon,
	default: payload.isDefault ?? false
});

async function fetchTypes(
	query: string,
	page: number,
	items: number,
	onResponse?: (totalPages: number, totalCount: number) => void
): Promise<IAnnouncementType[]> {
	try {
		const res = await get(`${BASE}${buildQuery(query, page, items)}`);
		const data = res.data as TypesResponse;
		onResponse?.(data.totalPages || 1, data.totalCount || 0);
		return (data.items || []).map(normalizeType);
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcementTypes.errorFetch'));
	}
}

async function postType(payload: IAnnouncementTypePayload): Promise<string | number> {
	try {
		const res = await post(BASE, { ...basePayload(payload), description: payload.description ?? null });
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcementTypes.errorCreate'));
	}
}

async function putType(
	typeId: number | string,
	payload: IAnnouncementTypePayload
): Promise<string | number> {
	try {
		const res = await put(`${BASE}/${typeId}`, {
			...basePayload(payload),
			description: payload.description ?? null,
			position: payload.position
		});
		return res?.status && res.status >= 200 && res.status < 300 ? 200 : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcementTypes.errorUpdate'));
	}
}

// GET api/announcement-types/icons → opțiunile de iconițe (value = nume @ant-design, label = etichetă).
async function fetchIconOptions(): Promise<{ value: string; label: string }[]> {
	try {
		const res = await get(`${BASE}/icons`);
		const data = Array.isArray(res.data) ? (res.data as { value?: string; label?: string }[]) : [];
		return data
			.map((o) => ({ value: String(o.value ?? ''), label: String(o.label ?? o.value ?? '') }))
			.filter((o) => o.value);
	} catch {
		return [];
	}
}

// GET api/announcement-types/table-config — configurația coloanelor tabelului de tipuri
// (chei, etichete, ordine, vizibilitate implicită). Se corelează în pagină cu definițiile
// locale prin applyServerColumnConfig.
async function fetchAnnouncementTypesTableConfig(): Promise<IServerColumnConfig[]> {
	try {
		const res = await get(`${BASE}/table-config`);
		const data = res.data as IServerTableConfig;
		return data?.columns ?? [];
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || 'Nu am reușit să încărcăm configurația tabelului.');
	}
}

async function deleteType(typeId: number | string): Promise<string | number> {
	try {
		const res = await del(`${BASE}/${typeId}`);
		return res?.status && res.status >= 200 && res.status < 300 ? res.status : 400;
	} catch (e) {
		const error = e as AxiosError;
		const data = error.response?.data as { error: string };
		throw new Error(data?.error || i18n.t('announcementTypes.errorDelete'));
	}
}

export {
	fetchTypes,
	fetchAnnouncementTypesTableConfig,
	postType,
	putType,
	deleteType,
	fetchIconOptions
};
